/**
 * Unlock grants → the integrator — docs/13 §6, docs/14 §8.2.
 *
 * `UnlockGrant` is an 11-member union and six of its members had no reader
 * anywhere in the codebase, so 31 of 66 unlocks fired, toasted "Unlocked — …"
 * and changed nothing. The compiler could not see it: every kind was handled
 * somewhere or nowhere, and `string` looks the same either way.
 *
 * This file is the one place that must name all eleven. The `never` assignment
 * in the `default` arm is what turns a twelfth kind into a **compile error**
 * instead of a silent no-op — add a member to the union and `tsc` fails here
 * until someone decides what it does.
 *
 * It lives in `core/` because that is the integrator layer: `meta/` may not
 * import the systems that consume its grants, and the consumers may not import
 * each other. This is where they meet.
 *
 * The kinds with no system yet route to `notImplemented`, which warns once per
 * kind. That is the deliberate half of the design: a gap that says its own name
 * on the console the first time a player earns it is a gap someone closes, and
 * an absent one is what produced this issue in the first place.
 *
 * Six kinds were pending when this file was written and **none remain**. Four
 * went first — `passive` (#4), `stash` (#5), `world-modifier` (#7) and
 * `recipes` (#8) — and `modules` (#9) and `rest-node` (#6) went last, each to a
 * module named on its arm below. The mechanism worked as designed every time:
 * `PENDING_GRANT_KINDS` is asserted two-way, so wiring a consumer without
 * deleting its entry failed the suite.
 *
 * The last two are worth a paragraph, because they are #81 and the lesson is
 * not the one the issue was filed about. Both entries were still *correct* as
 * entries — a `modules` grant really did reach `notImplemented` — while their
 * stated reasons had gone false underneath them. `modules` said
 * "`unlockedModuleIds()` has no caller" when `core/game.ts` had called it since
 * #9 closed; `rest-node` said "no rest-node entity exists" when
 * `world/restNode.ts` had existed since #6 closed. A two-way list guards
 * whatever it compares, and this one compared **keys**. The values were free
 * text nothing read, so the half a human reads — and the half printed to the
 * console — was the half that rotted.
 *
 * So the reason is no longer free text. `PendingGrantReason` carries the issue
 * as a number and the claim as `evidence` the suite can check against `src/`,
 * which means the entry now fails on the day the gap closes rather than on the
 * day someone re-reads it. See `tests/unlocks.test.ts`.
 */

import { UNLOCK_BY_ID } from '../meta/progression';
import type { Progression, UnlockGrant } from '../meta/progression';

export type GrantKind = UnlockGrant['kind'];

/**
 * What the unlock table actually granted this profile — a *raw* audit, before
 * any offer policy. `unlockEffects` is where floors and ordering are applied
 * (the default body is always selectable whether or not a grant says so); this
 * is the ledger of what the grants themselves said.
 */
export interface RunGrants {
  archetypes: string[];
  abilities: string[];
  attunementSlots: number;
  kits: string[];
  weaponClasses: string[];
  /** Passive ids — `meta/passives.ts` turns these into a flags object (#4). */
  passives: string[];
  /** Total gear slots that survive death — `meta/stash.ts` (#5). */
  stashSlots: number;
  /** Opt-in generator modifiers offered on the setup screen — `meta/worldModifiers.ts` (#7). */
  worldModifiers: string[];
  /** Recipe ids craftable at rest nodes — `meta/crafting.ts` (#8). */
  recipes: string[];
  /** Module families a drop may roll — `meta/unlockEffects.ts` (#9). */
  moduleFamilies: string[];
  /** Rest-node services the bench offers — `world/restNode.ts` (#6). */
  restNodes: string[];
  /**
   * Grant kinds that reached `notImplemented`, with the unlock ids holding
   * them. Non-empty is not an error — it is the size of the gap, in unlocks the
   * player has already earned and is not receiving.
   */
  pending: { kind: GrantKind; unlocks: string[] }[];
}

/**
 * The claim a pending entry makes about this codebase, in a form the suite can
 * check. This is the half that rotted (#81), so it is no longer prose.
 *
 * `no-caller` — the consumer exists and nothing calls it. The gap is the wiring.
 * `absent`    — the thing the design names does not exist in `src/` at all.
 *
 * Both are checked against masked source text in `tests/unlocks.test.ts`, and
 * both are anchored so that a misspelling cannot pass by being trivially true:
 * a `no-caller` symbol must actually be declared in the file it names, and an
 * `absent` one must be a term `docs/` describes.
 */
export type PendingEvidence =
  | { readonly claim: 'no-caller'; readonly symbol: string; readonly definedIn: string }
  | { readonly claim: 'absent'; readonly symbol: string };

export interface PendingGrantReason {
  /**
   * The **open** issue that closes this gap, as a number rather than inside the
   * prose. Two spellings of the same fact can disagree; one cannot. `#9` in the
   * old free-text reason went on being printed for months after #9 closed.
   */
  readonly issue: number;
  /** What is missing, for the console. Must not restate the issue number. */
  readonly why: string;
  /** The claim in `why`, as something the suite can check. */
  readonly evidence: PendingEvidence;
}

/**
 * Kinds with no system behind them yet, and the issue that closes each.
 *
 * This is a *contract*, not a note: `tests/unlocks.test.ts` asserts it matches
 * the kinds that actually reach `notImplemented`, in both directions. Wiring a
 * consumer without removing its entry here fails the suite, which is what stops
 * this list from drifting into fiction the way the grant table itself did.
 *
 * **Empty as of #81**, and that emptiness is the two-way check paying out for
 * the sixth time: `modules` and `rest-node` both had live consumers — the same
 * consumers their own reasons claimed did not exist — so their arms below now
 * record the grant and their entries had to go. Nothing routes to
 * `notImplemented` today. It stays exported, and tested, because a twelfth
 * grant kind is the thing it is for.
 */
export const PENDING_GRANT_KINDS: Readonly<Partial<Record<GrantKind, PendingGrantReason>>> = {};

export function emptyGrants(): RunGrants {
  return {
    archetypes: [],
    abilities: [],
    attunementSlots: 0,
    kits: [],
    weaponClasses: [],
    passives: [],
    stashSlots: 0,
    worldModifiers: [],
    recipes: [],
    moduleFamilies: [],
    restNodes: [],
    pending: [],
  };
}

/** Warned-about kinds, module-scoped so the console gets one line per kind ever. */
const warned = new Set<GrantKind>();

/**
 * A grant whose system does not exist. Named, exported and loud on purpose: the
 * whole failure this file addresses was a gap that made no sound.
 *
 * No arm of `applyGrant` routes here today — `PENDING_GRANT_KINDS` is empty —
 * so this is the landing pad the next unimplemented kind gets, exercised
 * directly by `tests/unlocks.test.ts` rather than through the dispatch.
 */
export function notImplemented(g: UnlockGrant, out: RunGrants, unlockId: string): void {
  const row = out.pending.find((p) => p.kind === g.kind);
  if (row) row.unlocks.push(unlockId);
  else out.pending.push({ kind: g.kind, unlocks: [unlockId] });

  if (warned.has(g.kind)) return;
  warned.add(g.kind);
  // The issue number comes off the field, not out of the prose. It is the same
  // number the suite asserts is still open, so the console line and the test
  // cannot disagree about which issue this is (#81).
  const r = PENDING_GRANT_KINDS[g.kind];
  const why = r ? `#${r.issue} — ${r.why}` : 'no consumer, and no entry in PENDING_GRANT_KINDS';
  console.warn(
    `[unlocks] grant kind "${g.kind}" has no system yet (${why}); ` +
      `${unlockId} is unlocked and grants nothing.`,
  );
}

/** Test seam: `warned` is module state, and a per-kind warning fires once ever. */
export function resetGrantWarnings(): void {
  warned.clear();
}

/**
 * The exhaustive dispatch. Every arm either records the grant for a consumer
 * that exists, or routes to `notImplemented`; the `default` arm cannot be
 * reached and exists only so that adding a kind stops compiling.
 */
export function applyGrant(g: UnlockGrant, out: RunGrants, unlockId = '<unknown>'): void {
  switch (g.kind) {
    // Read by `unlockedArchetypeIds` → the run-setup screen's body list.
    case 'archetype':
      out.archetypes.push(g.archetype);
      return;
    // Read by `attunedAbilities` → the run-setup screen's attunement picker.
    case 'attunement':
      out.abilities.push(g.ability);
      return;
    case 'attunement-slot':
      out.attunementSlots += g.slots;
      return;
    // Read by `unlockedKitIds` → the run-setup screen's kit list, and then by
    // `createRunState` → what the player is holding on frame one.
    case 'kit':
      out.kits.push(g.kit);
      return;
    // Read by `unlockedWeaponIds` → the altar's pool.
    case 'weapons':
      for (const c of g.classes) out.weaponClasses.push(c);
      return;

    // Read by `resolvePassives` → the flags object every consumer reads (#4).
    case 'passive':
      out.passives.push(g.passive);
      return;
    // Read by `Stash.capacity` → the profile-side gear store (#5).
    case 'stash':
      out.stashSlots += g.slots;
      return;
    // Read by `worldModifierChoices` → the setup screen's opt-in toggles (#7).
    case 'world-modifier':
      out.worldModifiers.push(g.modifier);
      return;
    // Read by `availableRecipes` → the rest-node crafting bench (#8). Deduped
    // (`Progression.unlockedRecipes()`'s own bucket already is, via a Set) —
    // #158 gave `splice-trigger` a second grantor (`ash.rest.reforge`,
    // alongside `salvage.craft.grafting`), a real case now rather than a
    // hypothetical one: a player who owns both must not have this summary
    // count the one recipe twice.
    case 'recipes':
      for (const r of g.recipes) if (!out.recipes.includes(r)) out.recipes.push(r);
      return;

    // Read by `unlockedModuleIds` → `rollModuleDrop`, the kill-drop pool that
    // `Game.enemyCtx.onDeath` rolls against (#9). The families are the gate;
    // `combat/moduleDrops.ts` never decides what a profile may hold.
    case 'modules':
      for (const f of g.families) out.moduleFamilies.push(f);
      return;
    // Read by `restServices` → what the bench offers when the player is
    // standing at a `world/restNode.ts` node (#6).
    case 'rest-node':
      out.restNodes.push(g.node);
      return;

    default: {
      const _exhaustive: never = g;
      return _exhaustive;
    }
  }
}

/** Walk everything this profile has unlocked through the dispatch above. */
export function resolveGrants(prog: Progression): RunGrants {
  const out = emptyGrants();
  for (const id of prog.state.unlocked) {
    const def = UNLOCK_BY_ID[id];
    // Content drift: `normaliseState` already drops unknown ids, so this is a
    // belt-and-braces skip rather than a case that should be reported.
    if (!def) continue;
    applyGrant(def.grants, out, id);
  }
  return out;
}

/**
 * One line for the event ticker. The pending clause was the point — a player who
 * owned three rest-node unlocks and had never seen a rest node could read that
 * off the log rather than infer it from the absence of rest nodes. It prints on
 * no real profile now, because nothing is pending; it stays for the next kind,
 * and `tests/unlocks.test.ts` keeps it exercised so it does not rot unwatched.
 */
export function describeGrants(g: RunGrants): string {
  const live =
    `${g.archetypes.length} bodies · ${g.kits.length} kits · ` +
    `${g.weaponClasses.length} weapon classes · ${g.attunementSlots} attunement slots · ` +
    `${g.passives.length} passives · ${g.stashSlots} stash slots · ` +
    `${g.worldModifiers.length} world modifiers · ${g.recipes.length} recipes · ` +
    `${g.moduleFamilies.length} module families · ${g.restNodes.length} rest services`;
  if (g.pending.length === 0) return live;
  const dead = g.pending.reduce((n, p) => n + p.unlocks.length, 0);
  return `${live} · ${dead} unlocks grant nothing yet (${g.pending.map((p) => p.kind).join(', ')})`;
}
